"use client";

import { useTranslations } from "next-intl";

// 设计指南数据项接口
export interface GuidelineItem {
	label: string;
	value: string;
	notes?: string;
}

// 组件 Props 接口
export interface GuidelineProps {
	translationNamespace?: string;
	title?: string;
	headerLabels?: {
		category?: string;
		recommendation?: string;
		notes?: string;
	};
	items?: GuidelineItem[];
	showNotes?: boolean;
}

// 默认设计指南数据配置（fiveAxisGuideline 使用）
const DEFAULT_GUIDELINE_KEYS = [
	{ labelKey: "minimumFeatureSize", valueKey: "minimumFeatureSizeValue" },
	{ labelKey: "linearDimensionTolerance", valueKey: "linearDimensionToleranceValue" },
	{ labelKey: "holeDiameters", valueKey: "holeDiametersValue" },
	{ labelKey: "shaftDiameters", valueKey: "shaftDiametersValue" },
	{ labelKey: "threadsAndTappedHoles", valueKey: "threadsAndTappedHolesValue" },
	{ labelKey: "engravedTextMarkings", valueKey: "engravedTextMarkingsValue" },
];

// CNC Milling 设计指南数据配置
const CNC_MILLING_GUIDELINE_KEYS = [
	{ labelKey: "radii", valueKey: "radiiValue", notesKey: "radiiNotes" },
	{ labelKey: "threads", valueKey: "threadsValue", notesKey: "threadsNotes" },
	{ labelKey: "wallThickness", valueKey: "wallThicknessValue", notesKey: "wallThicknessNotes" },
	{ labelKey: "textEngraving", valueKey: "textEngravingValue", notesKey: "textEngravingNotes" },
	{ labelKey: "holes", valueKey: "holesValue", notesKey: "holesNotes" },
];

// CNC Turning 设计指南数据配置
const CNC_TURNING_GUIDELINE_KEYS = [
	{ labelKey: "radii", valueKey: "radiiValue" },
	{ labelKey: "threads", valueKey: "threadsValue" },
	{ labelKey: "wallThickness", valueKey: "wallThicknessValue" },
	{ labelKey: "textEngraving", valueKey: "textEngravingValue" },
	{ labelKey: "holes", valueKey: "holesValue" },
];

export default function GurideLine({
	translationNamespace = "fiveAxisGuideline",
	title,
	headerLabels,
	items,
	showNotes = false,
}: GuidelineProps) {
	const t = useTranslations(translationNamespace);

	// 判断是否使用自定义数据
	const useCustomData = !!items && items.length > 0;

	// 根据 namespace 选择对应的数据配置
	const getGuidelineKeys = () => {
		switch (translationNamespace) {
			case "cncMillingGuideline":
				return CNC_MILLING_GUIDELINE_KEYS;
			case "cncTurningGuideline":
				return CNC_TURNING_GUIDELINE_KEYS;
			default:
				return DEFAULT_GUIDELINE_KEYS;
		}
	};
	const guidelineKeys = getGuidelineKeys();
	const shouldUseTitleLines =
		!title && ["cncTurningGuideline", "fiveAxisGuideline"].includes(translationNamespace);
	const titleText = title || (shouldUseTitleLines ? "" : t("title"));
	const titleLine1 = shouldUseTitleLines ? t("titleLine1") : "";
	const titleLine2 = shouldUseTitleLines ? t("titleLine2") : "";

	// 根据是否显示 notes 列决定表格布局
	const gridCols = showNotes
		? "grid-cols-1 sm:grid-cols-[25%_40%_35%] md:grid-cols-[20%_45%_35%]"
		: "grid-cols-1 sm:grid-cols-[30%_70%] md:grid-cols-[28%_72%]";

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 - 带装饰线 */}
				{shouldUseTitleLines ? (
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{titleLine1}</span>
						<span className="flex items-end gap-4">
							<span>{titleLine2}</span>
							<span className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
						</span>
					</h2>
				) : (
					<div className="flex flex-shrink-0 items-end gap-4">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{titleText}
						</h2>
						<div className="mb-1.5 h-1 w-12 flex-shrink-0 bg-themePrimary400 sm:mb-2 md:w-24" />
					</div>
				)}

				{/* 表格区域 */}
				<div className="mt-10 md:mt-14">
					<div className="w-full">
						{/* 表头行 */}
						<div className={`grid gap-4 border-b border-gray-200 pb-4 ${gridCols}`}>
							<div className="text-sm font-black text-black md:text-base">
								{headerLabels?.category || t("designFeature") || t("guidelineCategory")}
							</div>
							<div className="text-sm font-black text-black md:text-base">
								{headerLabels?.recommendation || t("recommendation") || t("recommendedSpecification")}
							</div>
							{showNotes && (
								<div className="text-sm font-black text-black md:text-base">
									{headerLabels?.notes || t("notes") || "Notes"}
								</div>
							)}
						</div>

						{/* 数据行 - 自定义数据模式 */}
						{useCustomData &&
							items.map((item, index) => (
								<div key={index} className={`grid gap-4 border-b border-gray-200 py-5 md:py-6 ${gridCols}`}>
									{/* 标签列 */}
									<div className="text-sm font-black text-black md:text-base">{item.label}</div>
									{/* 数值列 */}
									<div className="whitespace-pre-line text-sm !leading-relaxed text-black md:text-base">
										{item.value}
									</div>
									{/* Notes 列 */}
									{showNotes && (
										<div className="text-sm !leading-relaxed text-black md:text-base">
											{item.notes || "-"}
										</div>
									)}
								</div>
							))}

						{/* 数据行 - 翻译数据模式 */}
						{!useCustomData &&
							guidelineKeys.map((item) => (
								<div
									key={item.labelKey}
									className={`grid gap-4 border-b border-gray-200 py-5 md:py-6 ${gridCols}`}
								>
									{/* 标签列 */}
									<div className="text-sm font-black text-black md:text-base">{t(item.labelKey)}</div>
									{/* 数值列 */}
									<div className="whitespace-pre-line text-sm !leading-relaxed text-black md:text-base">
										{t(item.valueKey)}
									</div>
									{/* Notes 列 */}
									{showNotes && "notesKey" in item && (
										<div className="text-sm !leading-relaxed text-black md:text-base">
											{t(item.notesKey as string) || "-"}
										</div>
									)}
								</div>
							))}
					</div>
				</div>
			</div>
		</section>
	);
}
